import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from 'api';
import { useToast } from 'hooks/useToast';

/**
 * Interface for basic expectations payload
 */
export interface BasicExpectationsPayload {
  assessment_id: string;
  employment_type: string;
  experience_level: string;
  years_of_experience: string;
  work_mode: string;
  address: string | null;
  experience_environment: string;
  core_responsibilities: {
    [key: string]: string | boolean;
  };
  salary_range_term: string;
  salary_range_currency: string;
  min_salary_range_amount: number;
  max_salary_range_amount: number;
  require_work_mode?: boolean;
  require_salary_range?: boolean;
  display_salary?: boolean;
  require_portfolio_upload?: boolean;
}

/**
 * Helper function to parse validation errors from API response
 */
const parseValidationErrors = (errorData: Record<string, string[]> | undefined): string => {
  if (!errorData || typeof errorData !== 'object') {
    return '';
  }

  const fieldLabels: Record<string, string> = {
    min_salary_range_amount: 'Minimum salary',
    max_salary_range_amount: 'Maximum salary',
    employment_type: 'Employment type',
    work_mode: 'Work mode',
    address: 'Job location',
    experience_level: 'Experience level',
    years_of_experience: 'Years of experience',
    experience_environment: 'Experience environment',
    core_responsibilities: 'Core responsibilities',
    salary_range_term: 'Salary frequency',
    salary_range_currency: 'Salary currency',
    assessment: 'Assessment',
  };

  const errors: string[] = [];

  for (const [field, messages] of Object.entries(errorData)) {
    if (Array.isArray(messages) && messages.length > 0) {
      const label = fieldLabels[field] || field.replace(/_/g, ' ');
      errors.push(`${label}: ${messages[0]}`);
    }
  }

  return errors.join('. ');
};

/**
 * Hook for submitting basic expectations
 */
export const useBasicExpectations = (assessmentId: string | null) => {
  const queryClient = useQueryClient();
  const { openToast } = useToast();

  const mutation = useMutation({
    mutationFn: async (payload: BasicExpectationsPayload) => {
      const response = await apiClient.post('/assessment/basic-expectations/', payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['basicExpectations', assessmentId] });
    },
    onError: (err: any) => {
      // Try to extract specific validation errors from the response
      const validationErrors = parseValidationErrors(err.response?.data?.data);
      const assessmentError = err.response?.data?.data?.assessment?.[0];
      const genericMessage = err.response?.data?.message;

      // Prioritize specific validation errors, then assessment errors, then generic message
      const errorMessage = validationErrors || assessmentError || genericMessage || 'Failed to submit basic expectations';

      openToast({
        show: true,
        heading: "Sorry",
        text: errorMessage,
        type: "error",
        timeOut: 7000,
      });
    },
  });

  return {
    basicExpectationsResponse: mutation.data,
    isLoading: mutation.isPending,
    error: mutation.error,
    status: mutation.status,
    submitBasicExpectations: mutation.mutate,
  };
};
